import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@shared/stores/authStore';
import { useOperationalNavigationStore } from './operationalNavigationStore';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@shared/components/ui/shadcn/dialog';
import { Button } from '@shared/components/ui/button';

/** Retains the route subtree before an attempted destination can mount. */
export const OperationalRoutes = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const block = useOperationalNavigationStore((s) => s.block);
  const exit = useOperationalNavigationStore((s) => s.exit);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const inFlight = useRef(false);
  const identity = useAuthStore((s) => (s.token && s.user && s.team ? `${s.user.id}:${s.team.id}` : null));
  const pinned = block?.identity === identity ? block?.location : undefined;
  useLayoutEffect(() => {
    if (
      !pinned ||
      (location.pathname === pinned.pathname && location.search === pinned.search && location.hash === pinned.hash)
    )
      return;
    void navigate(
      { pathname: pinned.pathname, search: pinned.search, hash: pinned.hash },
      { replace: true, state: pinned.state },
    );
  }, [location, pinned, navigate]);
  const leaving = !pinned && exit?.identity === identity && exit.location.pathname !== location.pathname;
  useLayoutEffect(() => {
    if (!leaving) {
      if (failed) setFailed(false);
      return;
    }
    if (!exit || inFlight.current || failed) return;
    inFlight.current = true;
    void exit
      .finish()
      .then(() => {
        useOperationalNavigationStore.getState().releaseExit(exit.owner);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => {
        inFlight.current = false;
      });
  }, [leaving, exit, failed, retry]);
  return (
    <>
      <Routes location={pinned ?? (leaving ? exit.location : location)}>{children}</Routes>
      <Dialog open={Boolean(leaving && failed)}>
        <DialogContent
          hideCloseButton
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogTitle>{exit?.errorTitle}</DialogTitle>
          <DialogDescription>{exit?.errorDescription}</DialogDescription>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (exit) useOperationalNavigationStore.getState().releaseExit(exit.owner);
                setFailed(false);
              }}
            >
              {exit?.leaveLabel}
            </Button>
            <Button
              onClick={() => {
                setFailed(false);
                setRetry((value) => value + 1);
              }}
            >
              {exit?.retryLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
